import type { SidePolicy, WonderId } from '@sw/engine';
import { WONDER_IDS } from '@sw/engine';
import type { RoomSettings } from '@sw/protocol';
import { MAX_ROOM_PLAYERS, MIN_ROOM_PLAYERS } from '@sw/protocol';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { useMusic } from '../audio/useGameAudio';
import { WonderArt } from '../cards/WonderArt';
import { ChatPanel } from '../chat/ChatPanel';
import { errorText, wonderName } from '../i18n';
import { handleAck } from '../lib/errors';
import { emit } from '../net/socket';
import { useRoom } from '../stores/room';
import { useSession } from '../stores/session';
import { useUi } from '../stores/ui';
import { Button, Segmented, Spinner } from '../ui/Button';
import { CrownIcon } from '../ui/Icons';
import { Panel } from '../ui/Modal';
import ui from '../ui/ui.module.css';
import { Backdrop } from './Home';
import { NameEntry } from './NameEntry';
import s from './RoomLobby.module.css';

/**
 * Vista previa de la maravilla según el lado que fija la sala: Noche o Día si está decidido;
 * si cada uno elige (o va al azar), las dos versiones partidas en diagonal.
 */
function WonderPreview({ wonder, sidePolicy }: { wonder: WonderId; sidePolicy: SidePolicy }) {
  if (sidePolicy === 'day' || sidePolicy === 'night') return <WonderArt wonder={wonder} side={sidePolicy} />;
  return (
    <div className={s.splitPreview}>
      <WonderArt wonder={wonder} side="day" />
      <div className={s.splitNight}>
        <WonderArt wonder={wonder} side="night" />
      </div>
      <svg className={s.splitLine} viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
        <line x1="58" y1="0" x2="42" y2="100" vectorEffect="non-scaling-stroke" />
      </svg>
    </div>
  );
}

function useJoin(code: string) {
  const status = useSession((x) => x.status);
  const room = useRoom((r) => r.room);
  const [error, setError] = useState<{ code: string; params?: Record<string, string | number> } | null>(null);
  const tried = useRef<string | null>(null);
  useEffect(() => {
    if (status !== 'online' || room?.code === code || tried.current === code) return;
    tried.current = code;
    void emit('room:join', { code }).then((res) => {
      if (!res.ok) setError(res.error);
    });
  }, [status, code, room?.code]);
  return error;
}

function SettingsPanel({ settings, editable }: { settings: RoomSettings; editable: boolean }) {
  const { t } = useTranslation();
  const set = (patch: Partial<RoomSettings>) =>
    void emit('room:updateSettings', { settings: patch }).then(handleAck);
  const row = <K extends keyof RoomSettings>(key: K, values: RoomSettings[K][]) => (
    <div className={ui.field}>
      <span className={ui.label}>{t(`lobby.${key}`)}</span>
      <Segmented
        value={settings[key]}
        disabled={!editable}
        onChange={(v) => set({ [key]: v } as Partial<RoomSettings>)}
        options={values.map((v) => ({ value: v, label: t(`lobby.${key}_${v}`) }))}
      />
    </div>
  );
  return (
    <div className={s.settings}>
      {row('wonderMode', ['random', 'choose'])}
      {row('sidePolicy', ['choose', 'day', 'night', 'random'])}
      {row('seating', ['random', 'lobbyOrder'])}
      {row('visibility', ['private', 'public'])}
    </div>
  );
}

export function RoomLobby() {
  const { t } = useTranslation();
  const { code = '' } = useParams();
  const upper = code.toUpperCase();
  const navigate = useNavigate();
  const token = useSession((x) => x.token);
  const me = useSession((x) => x.user);
  const room = useRoom((r) => r.room);
  const joinError = useJoin(upper);
  const toast = useUi((u) => u.toast);
  useMusic('lobby');

  if (!token) {
    return (
      <div style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', padding: 16 }}>
        <Backdrop />
        <Panel style={{ padding: 24, width: 'min(100%, 420px)', position: 'relative' }}>
          <h2 style={{ color: 'var(--gold-strong)', marginBottom: 14 }}>
            {t('lobby.room')} {upper}
          </h2>
          <NameEntry />
        </Panel>
      </div>
    );
  }

  if (!room || room.code !== upper) {
    return (
      <div style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', padding: 16 }}>
        <Panel style={{ padding: 24, textAlign: 'center', maxWidth: 440 }}>
          {joinError ? (
            <>
              <p>{errorText(joinError.code, joinError.params)}</p>
              {joinError.code === 'ALREADY_IN_ROOM' && joinError.params?.code ? (
                <Button variant="primary" onClick={() => navigate(`/sala/${joinError.params!.code}`)}>
                  {t('home.backToRoom', { code: joinError.params.code })}
                </Button>
              ) : (
                <Link to="/">{t('common.goHome')}</Link>
              )}
            </>
          ) : (
            <>
              <Spinner /> {t('common.loading')}
            </>
          )}
        </Panel>
      </div>
    );
  }

  const isHost = room.hostId === me?.id;
  const amMember = room.members.some((m) => m.userId === me?.id);
  const n = room.members.length;
  const shareUrl = `${window.location.origin}/sala/${room.code}`;
  const picks = new Map(room.members.filter((m) => m.wonderPick).map((m) => [m.wonderPick as WonderId, m]));
  const myPick = room.members.find((m) => m.userId === me?.id)?.wonderPick ?? null;

  return (
    <div className={s.page}>
      <Panel className={s.section}>
        <div
          className={s.muted}
          style={{ textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.15em' }}
        >
          {t('lobby.room')}
        </div>
        <div className={s.code} data-testid="room-code">
          {room.code}
        </div>
        <p className={s.muted} style={{ textAlign: 'center', marginTop: 0 }}>
          {t('lobby.share')}
        </p>
        <Button
          style={{ width: '100%' }}
          onClick={() => {
            void navigator.clipboard
              ?.writeText(shareUrl)
              .then(() => toast('success', t('lobby.copied'), 2000));
          }}
        >
          🔗 {t('lobby.copyLink')}
        </Button>
        <h3 className={s.sectionTitle} style={{ marginTop: 22 }}>
          {t('lobby.settings')}
        </h3>
        <SettingsPanel settings={room.settings} editable={isHost && room.status === 'lobby'} />
        <div className={s.actions}>
          {room.status !== 'lobby' && room.gameId ? (
            <Button variant="primary" size="large" onClick={() => navigate(`/partida/${room.gameId}`)}>
              {t('lobby.backToGame')}
            </Button>
          ) : isHost ? (
            <Button
              variant="primary"
              size="large"
              disabled={n < MIN_ROOM_PLAYERS || n > MAX_ROOM_PLAYERS}
              onClick={() => void emit('room:start', {}).then(handleAck)}
              data-testid="start-game"
            >
              {t('lobby.start')}
            </Button>
          ) : (
            <p className={s.muted} style={{ textAlign: 'center' }}>
              {t('lobby.waitingHost')}
            </p>
          )}
          {n < MIN_ROOM_PLAYERS ? (
            <p className={s.muted} style={{ textAlign: 'center', margin: 0 }}>
              {t('lobby.needPlayers')}
            </p>
          ) : null}
          <Button
            variant="ghost"
            onClick={() =>
              void emit('room:leave', {}).then((r) => {
                if (handleAck(r)) navigate('/');
              })
            }
          >
            {t('lobby.leave')}
          </Button>
        </div>
      </Panel>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <Panel className={s.section}>
          <h3 className={s.sectionTitle}>{t('lobby.players', { count: n })}</h3>
          <div className={s.members} data-testid="members">
            {room.members.map((m) => (
              <div key={m.userId} className={s.member}>
                <div className={s.memberHead}>
                  <span
                    className={[s.dot, m.connected ? '' : s.dotOff].join(' ')}
                    title={m.connected ? '' : t('lobby.disconnected')}
                  />
                  <span className={s.memberName}>{m.username}</span>
                  {m.userId === room.hostId ? <CrownIcon size={18} title={t('lobby.host')} /> : null}
                  {m.userId === me?.id ? <span className={s.tag}>{t('lobby.you')}</span> : null}
                </div>
                <div className={s.memberThumb}>
                  {m.wonderPick ? (
                    <WonderPreview wonder={m.wonderPick} sidePolicy={room.settings.sidePolicy} />
                  ) : (
                    <div
                      style={{
                        height: '100%',
                        display: 'grid',
                        placeItems: 'center',
                        color: 'var(--text-faint)',
                        fontSize: 28,
                      }}
                    >
                      ?
                    </div>
                  )}
                </div>
                {isHost && m.userId !== me?.id && room.status === 'lobby' ? (
                  <div className={s.memberActions}>
                    <Button
                      size="small"
                      variant="ghost"
                      onClick={() => void emit('room:transferHost', { userId: m.userId }).then(handleAck)}
                    >
                      {t('lobby.makeHost')}
                    </Button>
                    <Button
                      size="small"
                      variant="danger"
                      onClick={() => void emit('room:kick', { userId: m.userId }).then(handleAck)}
                    >
                      {t('lobby.kick')}
                    </Button>
                  </div>
                ) : null}
              </div>
            ))}
            {Array.from({ length: Math.max(0, MIN_ROOM_PLAYERS - n) }, (_, i) => (
              <div key={`e${i}`} className={s.emptySeat}>
                …
              </div>
            ))}
          </div>
          {room.spectators.length ? (
            <p className={s.muted} style={{ marginBottom: 0 }}>
              {t('lobby.spectators')}: {room.spectators.map((x) => x.username).join(', ')}
            </p>
          ) : null}
        </Panel>

        {room.settings.wonderMode === 'choose' && room.status === 'lobby' && amMember ? (
          <Panel className={s.section}>
            <h3 className={s.sectionTitle}>{t('lobby.pickWonder')}</h3>
            <div className={s.wonders}>
              <button
                type="button"
                className={s.wonderPick}
                aria-pressed={myPick === null}
                onClick={() => void emit('room:pickWonder', { wonder: null }).then(handleAck)}
              >
                <div
                  className={s.wonderPickArt}
                  style={{ display: 'grid', placeItems: 'center', fontSize: 30 }}
                >
                  🎲
                </div>
                <div className={s.wonderPickLabel}>{t('lobby.noPick')}</div>
              </button>
              {WONDER_IDS.map((w) => {
                const owner = picks.get(w);
                const takenByOther = owner && owner.userId !== me?.id;
                return (
                  <button
                    key={w}
                    type="button"
                    className={s.wonderPick}
                    aria-pressed={myPick === w}
                    disabled={!!takenByOther}
                    title={takenByOther ? t('lobby.taken', { name: owner.username }) : wonderName(w, true)}
                    onClick={() => void emit('room:pickWonder', { wonder: w }).then(handleAck)}
                  >
                    <div className={s.wonderPickArt}>
                      <WonderPreview wonder={w} sidePolicy={room.settings.sidePolicy} />
                    </div>
                    <div className={s.wonderPickLabel}>{wonderName(w)}</div>
                  </button>
                );
              })}
            </div>
          </Panel>
        ) : null}
      </div>

      <Panel className={[s.section, s.chatBox].join(' ')}>
        <h3 className={s.sectionTitle}>{t('chat.title')}</h3>
        <ChatPanel />
      </Panel>
    </div>
  );
}
